import * as React from 'react'
import { AxiosError } from 'axios'
import { QueryStatus, QueryObserverOptions, UseQueryResult } from 'react-query'
import create, { State } from 'zustand'
import produce from 'immer'

import { useGremlinPagination, useGremlinQuery } from './use-gremlin-query'

import { PagedServiceResponse, Service } from 'api'

export interface ServicesState extends State {
  status: QueryStatus
  services: {
    [id: string]: Service
  }
  set: (fn: (state: ServicesState) => void) => void
  clearAll: () => void
}

export const useServicesStore = create<ServicesState>(set => ({
  status: 'loading',
  services: {},
  set: fn => set(produce(fn)),
  clearAll: () =>
    set(
      produce(state => {
        state.status = 'loading'
        state.services = {}
      })
    )
}))

export function useServices(enabled = true): void {
  const set = useServicesStore(state => state.set)
  const pagedAllFetchState = useGremlinPagination<PagedServiceResponse>(
    // Use long form endpoint to ensure react-query key cache hit
    `/prototype/services/paged?statusType=All&pageSize=20`,
    {},
    {
      onSuccess: data => {
        set(state => {
          const all = data?.pages?.flatMap(page => page.items)
          state.services = {
            ...state.services,
            ...Object.fromEntries(all.map(s => [s.id, s]))
          }
        })
      },
      onSettled: (data, error) => {
        set(state => {
          state.status =
            error && (!data || typeof data === 'string') ? 'error' : 'success'
        })
      },
      refetchInterval: 5 * 60_000,
      staleTime: 5 * 60_000,
      enabled
    }
  )

  React.useEffect(() => {
    let timeout
    if (
      pagedAllFetchState.hasNextPage &&
      !pagedAllFetchState.isFetchingNextPage
    ) {
      timeout = setTimeout(() => {
        pagedAllFetchState.fetchNextPage()
      }, 200)
    }
    return () => clearTimeout(timeout)
  }, [
    pagedAllFetchState.hasNextPage,
    pagedAllFetchState.fetchNextPage,
    pagedAllFetchState.isFetchingNextPage,
    pagedAllFetchState
  ])
  const pagedIgnoredFetchState = useGremlinPagination<PagedServiceResponse>(
    // Use long form endpoint to ensure react-query key cache hit
    `/prototype/services/paged?statusType=Ignored&pageSize=20`,
    {},
    {
      onSuccess: data => {
        set(state => {
          const ignored = data?.pages?.flatMap(page => page.items)
          state.services = {
            ...state.services,
            ...Object.fromEntries(ignored.map(s => [s.id, s]))
          }
        })
      },
      refetchInterval: 5 * 60_000,
      staleTime: 5 * 60_000,
      enabled
    }
  )

  React.useEffect(() => {
    let timeout
    if (
      pagedIgnoredFetchState.hasNextPage &&
      !pagedIgnoredFetchState.isFetchingNextPage
    ) {
      timeout = setTimeout(() => {
        pagedIgnoredFetchState.fetchNextPage()
      }, 200)
    }
    return () => clearTimeout(timeout)
  }, [
    pagedIgnoredFetchState.hasNextPage,
    pagedIgnoredFetchState.fetchNextPage,
    pagedIgnoredFetchState.isFetchingNextPage,
    pagedIgnoredFetchState
  ])
}

export function useGetService(
  id: string,
  queryOptions?: Partial<QueryObserverOptions<Service, AxiosError>>
): UseQueryResult<Service> {
  const set = useServicesStore(state => state.set)
  const initialData = useServicesStore(state => state.services[id])
  const serviceFetchState = useGremlinQuery<Service>(
    `/prototype/services/${id}`,
    {},
    {
      placeholderData: initialData,
      enabled: Boolean(id),
      ...queryOptions,
      onSuccess: data => {
        set(state => {
          state.services[data.id] = data
        })
      }
    }
  )

  return serviceFetchState
}
